import { Link } from 'react-router-dom';
import { Bot, ChevronRight, Loader2, Lock, Plus, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useAgents } from '../../hooks/useAgents';

/**
 * Chrome shared by every AI Agent page: breadcrumb, title, agent switcher, and the
 * gating states (no workspace / add-on off / no agents yet).
 *
 * `children` is a function receiving { agent, orgId, refresh } so the page only
 * renders once an agent is actually selected.
 */
const AgentPageShell = ({
  title,
  description,
  icon: Icon = Bot,
  breadcrumb = [],
  actions = null,
  requireAgent = true,
  children,
}) => {
  const { orgId, agents, meta, loading, selectedId, selectAgent, selectedAgent, refresh } =
    useAgents();

  const header = (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="space-y-1 min-w-0">
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>AI Agent</span>
          {breadcrumb.map((crumb) => (
            <span key={crumb} className="flex items-center gap-1.5">
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground font-medium">{crumb}</span>
            </span>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight truncate">{title}</h1>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {requireAgent && agents.length > 0 && (
          <Select value={selectedId || undefined} onValueChange={selectAgent}>
            <SelectTrigger className="w-[210px]" data-testid="agent-switcher">
              <SelectValue placeholder="Select an agent" />
            </SelectTrigger>
            <SelectContent>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {actions}
      </div>
    </div>
  );

  const wrap = (body) => (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-8">
      {header}
      {body}
    </div>
  );

  if (!orgId) {
    return wrap(
      <EmptyState
        icon={Lock}
        title="No workspace selected"
        body="Choose a workspace from the switcher at the top of the sidebar."
      />
    );
  }

  if (loading) {
    return wrap(
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading agents…
      </div>
    );
  }

  if (!meta.addonEnabled) {
    return wrap(
      <EmptyState
        icon={Sparkles}
        title="The AI Agent add-on isn't enabled"
        body="Turn on the AI Agent add-on to build assistants from your documentation spaces."
        action={
          <Button asChild>
            <Link to="/dashboard/pricing">View plans &amp; add-ons</Link>
          </Button>
        }
      />
    );
  }

  if (requireAgent && !selectedAgent) {
    return wrap(
      <EmptyState
        icon={Bot}
        title="No agents yet"
        body="Create an agent, point it at the spaces it should read, and deploy it into your app."
        action={
          <Button asChild>
            <Link to="/dashboard/agent/agents">
              <Plus className="w-4 h-4 mr-2" />
              Create your first agent
            </Link>
          </Button>
        }
      />
    );
  }

  return wrap(children({ agent: selectedAgent, orgId, refresh }));
};

const EmptyState = ({ icon: Icon, title, body, action }) => (
  <div className="rounded-xl border border-dashed border-border bg-card/40 py-20 px-6 text-center">
    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
      <Icon className="w-6 h-6 text-muted-foreground" />
    </div>
    <h3 className="font-semibold text-lg">{title}</h3>
    <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{body}</p>
    {action && <div className="mt-6">{action}</div>}
  </div>
);

export { EmptyState };
export default AgentPageShell;
